import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarX2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { formatLongDate, saTodayISO } from '../lib/dateUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schoolDaysBetween = (from, to) => {
  const days = [];
  const cur = new Date(`${from}T12:00:00Z`);
  const end = new Date(`${to}T12:00:00Z`);
  while (cur <= end && days.length < 60) {
    const dow = cur.getUTCDay();
    if (dow !== 0 && dow !== 6) days.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
};

export const AbsenceDialog = ({ kid, absences, token, onClose, onChanged }) => {
  const today = saTodayISO();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const kidAbsences = (absences || []).filter((a) => a.kid_id === kid?.kid_id);

  const submit = async () => {
    if (!from || !to || to < from) {
      toast.error('Please choose a valid start and end date.');
      return;
    }
    const dates = schoolDaysBetween(from, to);
    if (dates.length === 0) {
      toast.error('The dates you picked are all weekend days.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/parent/absences`, { kid_id: kid.kid_id, dates, reason }, { headers });
      if (res.data.created.length === 0) {
        toast.info('Those days are already marked absent.');
      } else {
        toast.success(`${res.data.message} Your driver has been notified.`);
      }
      setReason('');
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark absence');
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (absence) => {
    try {
      await axios.delete(`${API_URL}/parent/absences/${absence.absence_id}`, { headers });
      toast.success('Absence cancelled. Your driver has been notified.');
      onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel absence');
    }
  };

  return (
    <Dialog open={!!kid} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="absence-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarX2 className="w-5 h-5 text-amber-600" /> Mark {kid?.full_name} absent</DialogTitle>
          <DialogDescription>Whole days only. Weekends are skipped. Your driver and transport operator will see it in the app.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>From</Label>
            <Input type="date" min={today} value={from} onChange={(e) => { setFrom(e.target.value); if (e.target.value > to) setTo(e.target.value); }} data-testid="absence-from-input" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" min={from || today} value={to} onChange={(e) => setTo(e.target.value)} data-testid="absence-to-input" />
          </div>
        </div>
        <div>
          <Label>Reason (optional)</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Sick, family trip" data-testid="absence-reason-input" />
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Upcoming absences</p>
          {kidAbsences.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="absence-empty">No upcoming absences.</p>
          ) : (
            <ul className="space-y-2" data-testid="absence-list">
              {kidAbsences.map((a) => (
                <li key={a.absence_id} className="flex items-center justify-between gap-2 p-2 border rounded-lg bg-amber-50" data-testid={`absence-row-${a.date}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{formatLongDate(`${a.date}T12:00:00Z`)}</p>
                    {a.reason && <p className="text-xs text-muted-foreground truncate">{a.reason}</p>}
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-600 rounded-full" onClick={() => cancel(a)} data-testid={`absence-cancel-${a.date}`}>
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="absence-submit-btn">
            {saving ? 'Saving...' : 'Mark absent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
